import { useState, useMemo, memo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { computeQuestionNumbers } from '@/utils/surveyHelpers';
import { useBuilderTranslation } from '@/i18n/hooks/useTypedTranslation';
import type { Question, QuestionDisplayCondition, QuestionCategory } from '@/types';

// Check if a question can be used for conditional display
// Only questions with stored options can be used (the option ID must exist in the database)
function canBeUsedForCondition(question: Question): boolean {
  return question.options != null && question.options.length > 0;
}

interface ConditionBuilderProps {
  question: Question;
  allQuestions: Question[];
  categories: QuestionCategory[];
  conditions: QuestionDisplayCondition[];
  onChange: (conditions: QuestionDisplayCondition[]) => void;
  /** Effective display order for determining eligible questions (used in create mode with position selector) */
  effectiveDisplayOrder?: number;
}

interface ConditionRowProps {
  condition: QuestionDisplayCondition;
  availableQuestions: Question[];
  questionNumbers: Record<number, string>;
  onUpdate: (condition: QuestionDisplayCondition) => void;
  onDelete: () => void;
  t: ReturnType<typeof useBuilderTranslation>['t'];
}

function ConditionRow({
  condition,
  availableQuestions,
  questionNumbers,
  onUpdate,
  onDelete,
  t,
}: ConditionRowProps) {
  const dependsOnQuestion = availableQuestions.find(
    (q) => q.id === condition.dependsOnQuestionId
  );

  // Get options for the selected question
  const dependsOnOptions = dependsOnQuestion?.options || [];

  const selectedOption = dependsOnOptions.find(
    (o) => o.id === condition.requiredOptionId
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        p: 2,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: alpha('#14B8A6', 0.02),
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <AccountTreeIcon sx={{ color: 'text.secondary', mt: 1, fontSize: 20 }} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            gap: 2,
            minWidth: 0,
          }}
        >
          <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
            <InputLabel>{t('conditions.ifAnswerTo')}</InputLabel>
            <Select
              value={condition.dependsOnQuestionId || ''}
              label={t('conditions.ifAnswerTo')}
              onChange={(e) =>
                onUpdate({
                  ...condition,
                  dependsOnQuestionId: Number(e.target.value),
                  requiredOptionId: 0,
                })
              }
              MenuProps={{
                PaperProps: {
                  sx: { maxWidth: 400 },
                },
              }}
            >
              {availableQuestions.map((q) => (
                <MenuItem key={q.id} value={q.id} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {questionNumbers[q.id] || q.displayOrder}: {q.text.length > 35 ? `${q.text.substring(0, 35)}...` : q.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ArrowForwardIcon
            sx={{ display: { xs: 'none', sm: 'block' }, color: 'text.disabled', fontSize: 18, flexShrink: 0 }}
          />

          <FormControl size="small" sx={{ flex: 1, minWidth: 0 }} disabled={!dependsOnQuestion || dependsOnOptions.length === 0}>
            <InputLabel>{t('conditions.isEqualTo')}</InputLabel>
            <Select
              value={condition.requiredOptionId || ''}
              label={t('conditions.isEqualTo')}
              onChange={(e) =>
                onUpdate({ ...condition, requiredOptionId: Number(e.target.value) })
              }
            >
              {dependsOnOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {dependsOnQuestion && selectedOption && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, minWidth: 0, flexWrap: 'wrap' }}>
            <Chip
              label={dependsOnQuestion.text.length > 25 ? `${dependsOnQuestion.text.substring(0, 25)}...` : dependsOnQuestion.text}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 22, maxWidth: '100%' }}
            />
            <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Chip
              label={selectedOption.label}
              size="small"
              color="primary"
              sx={{ fontSize: '0.7rem', height: 22 }}
            />
          </Box>
        )}
      </Box>

      <IconButton
        size="small"
        onClick={onDelete}
        sx={{ color: 'error.main', '&:hover': { bgcolor: alpha('#ef4444', 0.1) } }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

// Memoized component to prevent unnecessary re-renders
export const ConditionBuilder = memo(function ConditionBuilder({
  question,
  allQuestions,
  categories,
  conditions,
  onChange,
  effectiveDisplayOrder,
}: ConditionBuilderProps) {
  const { t } = useBuilderTranslation();
  const [isEnabled, setIsEnabled] = useState(conditions.length > 0);

  // Compute question numbers for display
  const questionNumbers = useMemo(() => {
    return computeQuestionNumbers(allQuestions, categories);
  }, [allQuestions, categories]);

  // Use effective display order if provided (for create mode with position selector)
  // Otherwise use the question's actual display order
  const targetDisplayOrder = effectiveDisplayOrder ?? question.displayOrder;

  // Filter for questions that can be used for conditions:
  // - Not the current question
  // - Must come before the current question in display order
  // - Must have options (stored in database)
  const eligibleQuestions = useMemo(() => {
    return allQuestions.filter(
      (q) =>
        q.id !== question.id &&
        q.displayOrder < targetDisplayOrder &&
        canBeUsedForCondition(q)
    );
  }, [allQuestions, question.id, targetDisplayOrder]);

  const handleToggle = (enabled: boolean) => {
    setIsEnabled(enabled);
    if (!enabled) {
      onChange([]);
    }
  };

  const handleAddCondition = () => {
    // Use a small negative number for temporary IDs (must fit within Int32 range for C# backend)
    // Adding index offset ensures uniqueness when adding multiple conditions
    const tempId = -(conditions.length + 1);
    const newCondition: QuestionDisplayCondition = {
      id: tempId,
      questionId: question.id,
      dependsOnQuestionId: eligibleQuestions[0]?.id || 0,
      requiredOptionId: 0,
      isActive: true,
      createdDate: new Date().toISOString(),
    };
    onChange([...conditions, newCondition]);
  };

  const handleUpdateCondition = (
    index: number,
    updatedCondition: QuestionDisplayCondition
  ) => {
    const newConditions = [...conditions];
    newConditions[index] = updatedCondition;
    onChange(newConditions);
  };

  const handleDeleteCondition = (index: number) => {
    onChange(conditions.filter((_, i) => i !== index));
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: alpha('#14B8A6', 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AccountTreeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t('conditions.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('conditions.description')}
            </Typography>
          </Box>
        </Box>
        <Switch
          checked={isEnabled}
          onChange={(e) => handleToggle(e.target.checked)}
          color="primary"
        />
      </Box>

      {/* Content */}
      {isEnabled && (
        <Box
          sx={{
            pl: 2,
            borderLeft: 2,
            borderColor: alpha('#14B8A6', 0.2),
          }}
        >
          {eligibleQuestions.length === 0 ? (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{ '& .MuiAlert-message': { fontSize: '0.875rem' } }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t('conditions.noEligible')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('conditions.noEligibleHint')}
              </Typography>
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {conditions.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center', py: 2 }}
                >
                  {t('conditions.empty')}
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {conditions.length === 1
                      ? t('conditions.shownIfSingle')
                      : t('conditions.shownIfMultiple')}
                  </Typography>
                  {conditions.map((condition, index) => (
                    <ConditionRow
                      key={condition.id}
                      condition={condition}
                      availableQuestions={eligibleQuestions}
                      questionNumbers={questionNumbers}
                      onUpdate={(updated) => handleUpdateCondition(index, updated)}
                      onDelete={() => handleDeleteCondition(index)}
                      t={t}
                    />
                  ))}
                </>
              )}

              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddCondition}
                fullWidth
                sx={{ mt: 1 }}
              >
                {t('conditions.add')}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
});

export default ConditionBuilder;
